import { useState, useRef, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { 
  Sparkles, 
  Send, 
  Loader2, 
  FolderPlus, 
  MessageSquare, 
  Link2, 
  FileText,
  Lightbulb,
  CheckCircle,
  AlertCircle,
  Wand2
} from "lucide-react";

interface ParsedIntent {
  action: "create_space" | "create_source" | "start_chat" | "quick_capture" | "search" | "unknown";
  confidence: number;
  data: {
    title?: string;
    content?: string;
    url?: string;
    spaceName?: string;
    spaceId?: string;
    topicName?: string;
    topicId?: string;
    type?: string;
    tags?: string[];
  };
  suggestion: string;
}

interface QuickAddInputProps {
  placeholder?: string;
  onSuccess?: () => void;
  autoFocus?: boolean;
  showDialog?: boolean;
  onDialogChange?: (open: boolean) => void;
}

export function QuickAddInput({ 
  placeholder = "What would you like to do? Try: 'Add YouTube video about AI to my Research space'",
  onSuccess,
  autoFocus = false,
  showDialog = false,
  onDialogChange
}: QuickAddInputProps) {
  const [input, setInput] = useState("");
  const [parsedIntent, setParsedIntent] = useState<ParsedIntent | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: spaces = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["/api/spaces"],
  });

  const { data: topics = [] } = useQuery<{ id: string; name: string; spaceId: string }[]>({
    queryKey: ["/api/topics"],
  });

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const parseIntentMutation = useMutation({
    mutationFn: async (text: string): Promise<ParsedIntent> => {
      const res = await apiRequest("POST", "/api/quick-add/parse", {
        text,
        availableSpaces: spaces.map(s => ({ id: s.id, name: s.name })),
        availableTopics: topics.map(t => ({ id: t.id, name: t.name, spaceId: t.spaceId })),
      });
      if (!res.ok) throw new Error("Failed to parse intent");
      return res.json();
    },
    onSuccess: (data) => {
      setParsedIntent(data);
    },
    onError: () => {
      toast({
        title: "Couldn't understand that",
        description: "Try being more specific, like 'Add YouTube video to my Research space'",
        variant: "destructive",
      });
    },
  });

  const executeActionMutation = useMutation({
    mutationFn: async (intent: ParsedIntent) => {
      const res = await apiRequest("POST", "/api/quick-add/execute", { intent });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to execute action");
      }
      return res.json();
    },
    onSuccess: (result) => {
      setInput("");
      setParsedIntent(null);
      
      queryClient.invalidateQueries({ queryKey: ["/api/spaces"] });
      queryClient.invalidateQueries({ queryKey: ["/api/sources"] });
      queryClient.invalidateQueries({ queryKey: ["/api/chat-threads"] });
      queryClient.invalidateQueries({ queryKey: ["/api/quick-captures"] });

      toast({
        title: "Done!",
        description: result.message || "Action completed successfully",
      });

      if (result.redirect) {
        setLocation(result.redirect);
      }

      onSuccess?.();
      onDialogChange?.(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Action failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    if (!parsedIntent) {
      parseIntentMutation.mutate(input.trim());
    } else {
      executeActionMutation.mutate(parsedIntent);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setParsedIntent(null);
      setInput("");
      onDialogChange?.(false);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case "create_space": return <FolderPlus className="h-4 w-4" />;
      case "create_source": return <Link2 className="h-4 w-4" />;
      case "start_chat": return <MessageSquare className="h-4 w-4" />;
      case "quick_capture": return <Lightbulb className="h-4 w-4" />;
      case "search": return <FileText className="h-4 w-4" />;
      default: return <Sparkles className="h-4 w-4" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case "create_space": return "Create Space";
      case "create_source": return "Add Source";
      case "start_chat": return "Start Chat";
      case "quick_capture": return "Quick Capture";
      case "search": return "Search";
      default: return "Action";
    }
  };

  const inputContent = (
    <div className="w-full space-y-3">
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <Wand2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                setParsedIntent(null);
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="pl-10 pr-4 h-11 text-base"
              disabled={parseIntentMutation.isPending || executeActionMutation.isPending}
              data-testid="input-quick-add"
            />
          </div>
          <Button
            type="submit"
            size="default"
            disabled={!input.trim() || parseIntentMutation.isPending || executeActionMutation.isPending}
            data-testid="button-quick-add-submit"
          >
            {parseIntentMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : parsedIntent ? (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-2" />
                Parse
              </>
            )}
          </Button>
        </div>
      </form>

      {parsedIntent && (
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10 text-primary shrink-0">
                {getActionIcon(parsedIntent.action)}
              </div>
              <div className="flex-1 min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    {getActionLabel(parsedIntent.action)}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {Math.round(parsedIntent.confidence * 100)}% confident
                  </span>
                </div>
                <p className="text-sm">{parsedIntent.suggestion}</p>
                {parsedIntent.data.spaceName && (
                  <p className="text-xs text-muted-foreground">
                    Space: {parsedIntent.data.spaceName}
                  </p>
                )}
                {parsedIntent.data.url && (
                  <p className="text-xs text-muted-foreground truncate">
                    URL: {parsedIntent.data.url}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-3 justify-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setParsedIntent(null)}
                data-testid="button-quick-add-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={() => executeActionMutation.mutate(parsedIntent)}
                disabled={executeActionMutation.isPending}
                data-testid="button-quick-add-execute"
              >
                {executeActionMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Do it
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!parsedIntent && (
        <div className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
          <span>Try:</span>
          <button
            type="button"
            onClick={() => setInput("Create a new space called Project Alpha")}
            className="text-primary hover:underline"
            data-testid="suggestion-create-space"
          >
            "Create space called Project Alpha"
          </button>
          <span>•</span>
          <button
            type="button"
            onClick={() => setInput("Add a YouTube video about productivity")}
            className="text-primary hover:underline"
            data-testid="suggestion-add-video"
          >
            "Add YouTube video about productivity"
          </button>
          <span>•</span>
          <button
            type="button"
            onClick={() => setInput("Start a chat about marketing strategies")}
            className="text-primary hover:underline"
            data-testid="suggestion-start-chat"
          >
            "Chat about marketing"
          </button>
        </div>
      )}
    </div>
  );

  if (!showDialog) {
    return null;
  }

  return (
    <Dialog open={showDialog} onOpenChange={onDialogChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5" />
            Quick Add
          </DialogTitle>
          <DialogDescription>
            Type what you want to do in plain language. AI will understand and execute it.
          </DialogDescription>
        </DialogHeader>
        {inputContent}
      </DialogContent>
    </Dialog>
  );
}

export function QuickAddTrigger() {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsOpen(true)}
            data-testid="button-quick-add-trigger"
          >
            <Wand2 className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Quick Add</TooltipContent>
      </Tooltip>
      <QuickAddInput
        showDialog={isOpen}
        onDialogChange={setIsOpen}
        autoFocus
      />
    </>
  );
}
